import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import OneVerseLogo from '@/components/OneVerseLogo';
import { API } from '@/utils/API';
import { useUser } from '@/providers/UserProvider';
import { useTermsData } from '@/hooks/useTermsData';

interface PendingTerms {
  id: string;
  title: string;
  version: string;
  effective_date: string;
  content?: string;
}

interface TermsGateProps {
  pendingTerms: PendingTerms;
  onAccepted: () => void;
}

const TermsGate: React.FC<TermsGateProps> = ({ pendingTerms, onAccepted }) => {
  const { termsData, loading, error: termsError } = useTermsData();
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [acceptanceChecked, setAcceptanceChecked] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAcceptTerms = async () => {
    try {
      setAccepting(true);
      setError(null);

      const response = await API.authenticated.fetch('/terms', {
        method: 'POST',
      });

      if (response.ok) {
        onAccepted();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || 'Failed to accept terms');
      }
    } catch (e) {
      console.error('Error accepting terms:', e);
      setError('Failed to accept terms');
    } finally {
      setAccepting(false);
    }
  };

  const canAccept = hasScrolledToBottom && acceptanceChecked && !accepting;
  const displayError = error || termsError;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <OneVerseLogo className="mb-6 mt-2 mx-auto" />
          <h1 className="text-3xl font-semibold text-gray-900 mb-2">
            New Terms and Conditions
          </h1>
          <p className="text-gray-600">
            Please review and accept the updated terms to continue using the
            application.
          </p>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="bg-blue-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {termsData?.title || pendingTerms.title}
            </h2>
            <div className="mt-1 text-sm text-gray-600">
              Version {termsData?.version || pendingTerms.version} • Effective{' '}
              {new Date(
                termsData?.effective_date || pendingTerms.effective_date
              ).toLocaleDateString()}
            </div>
          </div>

          {loading && (
            <div className="p-8 text-center">
              <div className="text-gray-600">
                Loading terms and conditions...
              </div>
            </div>
          )}

          {displayError && (
            <div className="p-8 text-center">
              <div className="text-red-600">{displayError}</div>
            </div>
          )}

          {termsData?.content && !loading && (
            <>
              <div
                className="p-6 max-h-96 overflow-y-auto"
                onScroll={handleScroll}
              >
                <div className="prose prose-gray max-w-none prose-headings:font-semibold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-gray-600 prose-li:text-gray-600 prose-a:text-blue-600 hover:prose-a:text-blue-800">
                  <ReactMarkdown>{termsData.content}</ReactMarkdown>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <div className="flex items-center space-x-3 mb-4">
                  <Checkbox
                    id="accept-terms"
                    checked={acceptanceChecked}
                    onCheckedChange={checked =>
                      setAcceptanceChecked(checked === true)
                    }
                    disabled={!hasScrolledToBottom}
                  />
                  <label
                    htmlFor="accept-terms"
                    className={`text-sm font-medium ${
                      hasScrolledToBottom ? 'text-gray-900' : 'text-gray-400'
                    }`}
                  >
                    I have read and agree to the Terms and Conditions
                  </label>
                </div>

                {!hasScrolledToBottom && (
                  <p className="text-xs text-gray-500 mb-4">
                    Please scroll to the bottom of the terms to enable
                    acceptance.
                  </p>
                )}

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <div className="flex justify-end">
                  <Button
                    onClick={handleAcceptTerms}
                    disabled={!canAccept}
                    className="px-6 py-2"
                  >
                    {accepting ? 'Accepting...' : 'Accept and Continue'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mt-6 text-center text-sm text-gray-500">
          You must accept the terms and conditions to continue using the
          application.
        </div>
      </div>
    </div>
  );
};

export const TermsGateWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { userData, loading } = useUser();

  // Show loading while user data is being fetched
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Impersonation sessions bypass the terms gate for the same reason
  // we bypass MFA setup — the admin is here to debug what comes after
  // onboarding, not to accept terms on the impersonated user's behalf.
  if (
    userData?.has_pending_terms
    && userData.pending_terms
    && !userData.impersonating
  ) {
    return (
      <TermsGate
        pendingTerms={userData.pending_terms}
        onAccepted={() => window.location.reload()} // Reload to refetch user data
      />
    );
  }

  // Otherwise, show the normal app content
  return <>{children}</>;
};

export default TermsGate;
